import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from './QuizModa.module.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { submitQuizSolution } from '../../../../../redux/actions/quiz';
import { CircularProgress } from '@mui/material';
import { getCourseProgress } from '../../../../../redux/actions/courses';

// Improvements after UX review:
// - Checkbox inputs now render visually as checkboxes (separate CSS class)
// - Fill-in-the-blank renders like MCQ (choices list) by default (per your request)
// - Grid renders as a table (rows x columns) for clearer UI and stores a mapping object
// - Image choices have an onError fallback and larger thumbnails
// - Submission builder understands mapping objects for grid and preserves previous nested format for FIB

const typeMap = {
  mcq: 'mcq',
  multipleChoice: 'mcq',
  checkboxes: 'checkboxes',
  checkbox: 'checkboxes',
  fillInBlanks: 'fillInBlanks',
  fillBlanks: 'fillInBlanks',
  Fib: 'fillInBlanks',
  trueFalse: 'trueFalse',
  truefalse: 'trueFalse',
  grid: 'grid',
  image: 'image',
  imageChoice: 'image',
  imagechoice: 'image',
};
const normalizeType = (t) => (t ? typeMap[t] || t : 'mcq');

const QuizModal = ({ onClose, quizData, onSubmissionSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = useSelector((state) => state?.quiz?.isLoading || false);
  const questions = quizData?.template?.questions || [];

  // count answered questions by checking either text or selected state per question
  const answeredQuestions = questions.reduce((acc, question) => {
    const qid = String(question.id);
    const qType = normalizeType(question.question_type);
    const hasText = (textAnswers[qid] || '').toString().trim().length > 0;
    const sel = selectedAnswers[qid];
    const hasSelected = Array.isArray(sel) ? sel.length > 0 : sel !== undefined && sel !== null && (typeof sel === 'object' ? Object.keys(sel).length > 0 : String(sel) !== '');
    if (hasText || hasSelected) return acc + 1;
    return acc;
  }, 0);

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];

  useEffect(() => {
    // Prefill existing submission answers (if any) into local state
    const existing = quizData?.user_submission?.user_answers || [];
    if (existing && existing.length) {
      const sel = {};
      const txt = {};
      existing.forEach((a) => {
        const qid = String(a.question);
        if (Array.isArray(a.chosen_answer) && a.chosen_answer.length) {
          if (Array.isArray(a.chosen_answer[0])) {
            // [[choiceId, text]] pattern
            const pair = a.chosen_answer[0];
            sel[qid] = pair[0];
            txt[qid] = pair[1] ?? '';
          } else {
            // flat array of ids
            if (a.chosen_answer.length === 1) sel[qid] = a.chosen_answer[0];
            else sel[qid] = a.chosen_answer.slice();
          }
        }
      });
      setSelectedAnswers((prev) => ({ ...prev, ...sel }));
      setTextAnswers((prev) => ({ ...prev, ...txt }));
    }
  }, [quizData]);

  function getImageUrl(path) {
    if (!path) return null;

    // Already absolute
    if (/^https?:\/\//.test(path) || path.startsWith("blob:") || path.startsWith("data:")) {
      return path;
    }

    let base = window.location.origin;

    // Special-case local dev
    if (base === "http://local.nxnearby.com:3000") {
      base = "https://stage.nxnearby.com";
    }

    // Ensure /media prefix
    if (!path.startsWith("/media/")) {
      path = "/media/" + path.replace(/^\/+/, "");
    }

    return `${base}${path}`;
  }

  const handleAnswerSelect = (question, choiceId, rowId = null) => {
    const qid = String(question.id);
    const qType = normalizeType(question.question_type);

    if (qType === 'checkboxes') {
      setSelectedAnswers((prev) => {
        const arr = Array.isArray(prev[qid]) ? [...prev[qid]] : [];
        const idx = arr.findIndex((x) => String(x) === String(choiceId));
        if (idx >= 0) arr.splice(idx, 1);
        else arr.push(choiceId);
        return { ...prev, [qid]: arr };
      });
      setTextAnswers((prev) => {
        if (!prev[qid]) return prev;
        const n = { ...prev };
        delete n[qid];
        return n;
      });
      return;
    }

    if (qType === 'grid') {
      // rowId must be provided (the row choice id). choiceId is the selected column label or id.
      setSelectedAnswers((prev) => {
        const map = { ...(prev[qid] || {}) };
        map[rowId] = choiceId; // store chosen label or id per row
        return { ...prev, [qid]: map };
      });
      return;
    }

    // default single-select (mcq, trueFalse, image, fillInBlanks when rendered as MCQ)
    setSelectedAnswers((prev) => ({ ...prev, [qid]: choiceId }));
    setTextAnswers((prev) => {
      if (!prev[qid]) return prev;
      const n = { ...prev };
      delete n[qid];
      return n;
    });
  };

  const handleTextAnswerChange = (questionId, value) => {
    const qid = String(questionId);
    setTextAnswers((prev) => ({ ...prev, [qid]: value }));
    setSelectedAnswers((prev) => {
      if (!prev[qid]) return prev;
      const n = { ...prev };
      delete n[qid];
      return n;
    });
  };

  const handleNextQuestion = () => { if (currentQuestionIndex < questions.length - 1) setCurrentQuestionIndex((p) => p + 1); };
  const handlePreviousQuestion = () => { if (currentQuestionIndex > 0) setCurrentQuestionIndex((p) => p - 1); };

  const buildSubmissionPayload = () => {
    const payload = questions.map((question) => {
      const qid = String(question.id);
      const qType = normalizeType(question.question_type);
      const sel = selectedAnswers[qid];
      const txt = textAnswers[qid] ?? '';

      if (qType === 'fillInBlanks') {
        // keep nested format if text present
        if (txt && question.choice && question.choice.length) {
          const choiceId = question.choice[0]?.id;
          return { question: qid, chosen_answer: [[Number(choiceId), txt]], time_left: 0 };
        }
        if (sel) return Array.isArray(sel) ? { question: qid, chosen_answer: sel.map(Number), time_left: 0 } : { question: qid, chosen_answer: [Number(sel)], time_left: 0 };
        return null;
      }

      if (qType === 'checkboxes') {
        if (!sel || (Array.isArray(sel) && sel.length === 0)) return null;
        return { question: qid, chosen_answer: sel.map(Number), time_left: 0 };
      }

      if (qType === 'grid') {
        const map = sel || {};
        // convert mapping { rowId: chosenLabel } into array of pairs [rowId, chosenLabel]
        const pairs = Object.keys(map).map((rowId) => [Number(rowId), map[rowId]]);
        if (pairs.length === 0) return null;
        return { question: qid, chosen_answer: pairs, time_left: 0 };
      }

      if (sel === undefined || sel === null || sel === '') return null;
      if (Array.isArray(sel)) return { question: qid, chosen_answer: sel.map(Number), time_left: 0 };
      return { question: qid, chosen_answer: [Number(sel)], time_left: 0 };
    });

    return payload.filter((p) => p !== null);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const possibleSubmissionId = quizData?.user_submission?.id || quizData?.submission_id || quizData?.submissionId || quizData?.quiz_submission_id;
      if (!possibleSubmissionId) { alert('Unable to submit quiz: No submission ID found. Please refresh and try again.'); return; }

      const userAnswers = buildSubmissionPayload();
      if (!userAnswers.length) { alert('Please answer at least one question before submitting.'); return; }

      const submissionData = { submissionId: possibleSubmissionId, userAnswers };
      const response = await dispatch(submitQuizSolution(submissionData));
      const updated = response?.payload?.data?.update_quiz_submission || response?.data?.update_quiz_submission || response?.update_quiz_submission || response;
      if (!updated) return;

      const courseId = quizData?.course_id || quizData?.course?.id || quizData?.courseId || quizData?.quiz?.course_id;
      if (courseId) { try { await dispatch(getCourseProgress(courseId)); } catch (e) { console.warn('Failed to refresh progress', e); } }

      if (onSubmissionSuccess) { try { onSubmissionSuccess(response); } catch (e) { console.warn('onSubmissionSuccess threw', e); } }
      onClose();
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('An error occurred while submitting the quiz. Please try again.');
    } finally { setIsSubmitting(false); }
  };

  // Render helpers
  const renderChoice = (question, choice) => {
    const qid = String(question.id);
    const qType = normalizeType(question.question_type);
    const sel = selectedAnswers[qid];
    const isChecked = Array.isArray(sel) ? sel.some((s) => String(s) === String(choice.id)) : String(sel) === String(choice.id);
    const inputType = qType === 'checkboxes' ? 'checkbox' : 'radio';
    const inputClass = qType === 'checkboxes' ? styles.optionCheckbox : styles.optionRadio;

    if (qType === 'image') {
      return (
        <label key={choice.id} className={styles.optionLabel}>
          <input type={inputType} name={`question-${qid}`} checked={isChecked} onChange={() => handleAnswerSelect(question, choice.id)} disabled={isSubmitting || isLoading} className={inputClass} />
          <div className={styles.imageOption}>
            {choice.img ? (
              <img
                src={getImageUrl(choice.img)}
                alt={`option-${choice.id}`}
                className={styles.choiceImage}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className={styles.imagePlaceholder}>No Image</div>
            )}
            {choice.choice_text ? <div className={styles.optionText}>{choice.choice_text}</div> : null}
          </div>
        </label>
      );
    }

    return (
      <label key={choice.id} className={styles.optionLabel}>
        <input type={inputType} name={`question-${qid}`} checked={isChecked} onChange={() => handleAnswerSelect(question, choice.id)} className={inputClass} disabled={isSubmitting || isLoading} />
        <span className={styles.optionText}>{choice.choice_text ?? choice.text ?? String(choice.id)}</span>
      </label>
    );
  };

  const renderQuestionInput = (question) => {
    if (!question) return null;
    const qType = normalizeType(question.question_type);

    if (qType === 'fillInBlanks') {
      // Render like MCQ (list of choices) per your request; fallback to text input if no choices
      if (question.choice && question.choice.length) {
        return <div className={styles.options}>{question.choice.map((c) => renderChoice(question, c))}</div>;
      }
      return (
        <div className={styles.fillInBlankContainer}>
          <input type="text" placeholder="Type your answer" value={textAnswers[String(question.id)] || ''} onChange={(e) => handleTextAnswerChange(question.id, e.target.value)} disabled={isSubmitting || isLoading} className={styles.fillInText} />
        </div>
      );
    }

    if (qType === 'grid') {
      // Build table: columns = unique correct_choice labels, rows = question.choice
      const labels = Array.from(new Set((question.choice || []).map((c) => c.correct_choice).filter(Boolean)));
      const qid = String(question.id);
      if (labels.length > 0) {
        return (
          <div className={styles.gridContainer}>
            <table className={styles.gridTable}>
              <thead>
                <tr>
                  <th />
                  {labels.map((lab) => (<th key={lab}>{lab}</th>))}
                </tr>
              </thead>
              <tbody>
                {(question.choice || []).map((row) => (
                  <tr key={row.id}>
                    <td className={styles.gridRowLabel}>{row.choice_text ?? String(row.id)}</td>
                    {labels.map((lab) => (
                      <td key={lab} className={styles.gridCell}>
                        <input type="radio" name={`grid-${qid}-${row.id}`} checked={String(selectedAnswers[qid]?.[row.id] ?? '') === String(lab)} onChange={() => handleAnswerSelect(question, lab, row.id)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      // fallback to MCQ-like if no labels
      return <div className={styles.options}>{(question.choice || []).map((c) => renderChoice(question, c))}</div>;
    }

    // default
    return <div className={styles.options}>{(question.choice || []).map((c) => renderChoice(question, c))}</div>;
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <button className={styles.backButton} onClick={onClose} disabled={isSubmitting || isLoading}><ArrowBackIcon /></button>
            <div className={styles.quizIcon}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#f33070" /><path d="M7 12H17M7 8H17M7 16H13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
            <h2>{quizData?.title || quizData?.template?.title || 'Quiz'}</h2>
          </div>
          <button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting || isLoading || answeredQuestions === 0}>
            {isSubmitting || isLoading ? (<><CircularProgress color="inherit" size={16} /><span style={{ marginLeft: '8px' }}>SUBMITTING...</span></>) : ('SUBMIT MY ANSWERS')}
          </button>
        </div>

        <div className={styles.content}>
          <p className={styles.questionCounter}>{answeredQuestions} of {totalQuestions} Questions Answered</p>

          {currentQuestion && (
            <div className={styles.questionCard}>
              <div className={styles.questionHeader}>
                <div className={styles.questionType}>{currentQuestion.text}</div>
                <div className={styles.marks}>{currentQuestion.marks} Marks</div>
              </div>

              <div className={styles.questionContent}>{renderQuestionInput(currentQuestion)}</div>
            </div>
          )}

          <div className={styles.pagination}>
            <button className={styles.pageButton} onClick={handlePreviousQuestion} disabled={currentQuestionIndex === 0 || isSubmitting || isLoading}>&lt;</button>
            <span className={styles.pageNumber}>{currentQuestionIndex + 1}</span>
            <button className={styles.pageButton} onClick={handleNextQuestion} disabled={currentQuestionIndex === questions.length - 1 || isSubmitting || isLoading}>&gt;</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizModal;
